import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "../styles/ExportAliasManager.css";

const EmailAliasManager = ({ onTutorialClick }) => {
  const [aliasEmail, setAliasEmail] = useState("");
  const [existingAlias, setExistingAlias] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);

  // Fetch current alias on component mount
  useEffect(() => {
    const fetchAlias = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get("/api/alias", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.data.success && response.data.aliasEmail) {
          setExistingAlias(response.data.aliasEmail);
        }
      } catch (error) {
        console.error("Error fetching alias email:", error);
        toast.error("Unable to fetch alias email");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlias();
  }, []);

  const handleAliasSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        "/api/alias/request",
        { aliasEmail },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setPendingConfirmation(true);
        toast.success("Confirmation email sent! Check your inbox to confirm.");
      }
    } catch (error) {
      console.error("Error requesting alias email:", error);
      const errorMessage =
        error.response?.data?.message || "Error adding alias email";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAliasRemove = async () => {
    if (!window.confirm("Are you sure you want to remove this alias email?")) {
      return;
    }

    setIsRemoving(true);

    try {
      const response = await axios.delete("/api/alias", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data.success) {
        setExistingAlias("");
        toast.success("Alias email removed successfully");
      }
    } catch (error) {
      console.error("Error removing alias email:", error);
      toast.error("Error removing alias email");
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="settings-card">
        <div className="settings-card-header">
          <i className="fas fa-envelope settings-card-icon"></i>
          <h4 className="settings-card-title">
            Email Alias
            <i
              className="fas fa-question-circle tutorial-icon"
              onClick={onTutorialClick}
              title="How to manage Email Aliases"
              style={{ marginLeft: "8px", cursor: "pointer" }}
            ></i>
          </h4>
        </div>
        <div className="settings-card-description">
          <p>Loading email alias information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <i className="fas fa-envelope settings-card-icon"></i>
        <h4 className="settings-card-title">
          Email Alias
          <i
            className="fas fa-question-circle tutorial-icon"
            onClick={onTutorialClick}
            title="How to manage Email Aliases"
            style={{ marginLeft: "8px", cursor: "pointer" }}
          ></i>
        </h4>
      </div>
      <div className="settings-card-description">
        <p>
          Add an alias email address that you can use to forward receipts to
          receipts@expensebot.ai. This allows you to use multiple email
          addresses with your ExpenseBot account.
        </p>

        {existingAlias ? (
          <div className="alias-display">
            <div className="alias-active">
              <span>
                <strong>Current alias:</strong> {existingAlias}
              </span>
              <button
                onClick={handleAliasRemove}
                disabled={isRemoving}
                className="settings-danger-button alias-remove-button"
              >
                {isRemoving ? "Removing..." : "Remove Alias"}
              </button>
            </div>
          </div>
        ) : pendingConfirmation ? (
          <div className="alias-pending">
            <p>
              We've sent a confirmation email to <strong>{aliasEmail}</strong>.
            </p>
            <p>
              Please check your inbox and click the confirmation link to
              activate your alias.
            </p>
            <button
              onClick={() => setPendingConfirmation(false)}
              className="settings-card-link"
            >
              Try Another Email
            </button>
          </div>
        ) : (
          <form onSubmit={handleAliasSubmit} className="alias-form">
            <div className="form-group">
              <input
                type="email"
                value={aliasEmail}
                onChange={(e) => setAliasEmail(e.target.value)}
                placeholder="Enter alias email address"
                className="alias-input"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="settings-card-link alias-submit"
              >
                {isSubmitting ? "Sending..." : "Add Alias"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EmailAliasManager;
